import { useToast } from '@chakra-ui/react';
import React, { useMemo, useRef, useState } from 'react';

import {
  baixarArquivoDaImportacao,
  baixarRelatorioDaImportacao,
  cancelarImportacao,
  type IAnoAntesEDepois,
  type IConferenciaDoMes,
  type IRegistroDaImportacao,
} from '../../../../../hooks/useFechamentoContabilMensal';
import { numero } from '../regrasDaCarteira';
import {
  ACOES_DO_SISTEMA,
  cnpjFormatado,
  COLUNAS_DA_CONFERENCIA,
  confere,
  daCompetencia,
  dataCurta,
  mesAno,
  MOTIVOS,
  motivosDeFora,
  pct,
  porEmpresa,
  somaConfere,
  tamanho,
  totaisDo,
  totalDaConferencia,
  variacao,
} from '../regrasDaImportacao';
import { dataEHora } from '../regrasDoFechamento';
import { Baixar } from './icones';

const MOTIVO_MINIMO = 5;

export function mensagemDoErro(erro: unknown): string {
  const detalhe = (erro as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detalhe === 'string' ? detalhe : 'Tente de novo em instantes.';
}

function Kpi({
  rotulo,
  valor,
  detalhe,
  cor,
}: {
  rotulo: string;
  valor: number;
  detalhe?: string;
  cor: string;
}) {
  return (
    <div className="impKpi" style={{ ['--cor' as string]: cor }}>
      <span className="impKpiRot">{rotulo}</span>
      <span className="impKpiNum">{numero(valor)}</span>
      {detalhe && <span className="impKpiDet">{detalhe}</span>}
    </div>
  );
}

function Barra({
  antes,
  depois,
  grande,
}: {
  antes: number | null;
  depois: number | null;
  grande?: boolean;
}) {
  return (
    <div
      className={`impBarra${grande ? ' grande' : ''}`}
      role="img"
      aria-label={`Fechado: antes ${pct(antes)}, depois ${pct(depois)}`}
    >
      <span className="depois" style={{ width: `${Math.min(100, depois ?? 0)}%` }} />
      <span className="antes" style={{ width: `${Math.min(100, antes ?? 0)}%` }} />
    </div>
  );
}

/**
 * Linha de mês que seleciona a competência (as listas de baixo filtram por ela). Clicar
 * de novo no mesmo mês limpa o filtro.
 */
const linhaDoMes = (
  competencia: string,
  mes: string | undefined,
  onMes: ((c: string) => void) | undefined,
) =>
  onMes
    ? {
        role: 'button',
        tabIndex: 0,
        className: `clic${mes === competencia ? ' sel' : ''}`,
        'aria-pressed': mes === competencia,
        title:
          mes === competencia
            ? 'Clique para ver todas as competências'
            : `Ver o detalhe de ${mesAno(competencia)} nas listas abaixo`,
        onClick: () => onMes(mes === competencia ? '' : competencia),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onMes(mes === competencia ? '' : competencia);
          }
        },
      }
    : {};

/** Antes x depois do calendário: o ano em destaque e a tabela mês a mês. */
export function AntesEDepois({
  anos,
  titulo,
  mes,
  onMes,
}: {
  anos: IAnoAntesEDepois[];
  titulo: string;
  mes?: string;
  onMes?: (competencia: string) => void;
}) {
  if (!anos.length) return null;
  return (
    <div className="impSecao">
      <span className="impSub">
        {titulo}
        {onMes && <span className="impDica"> · clique num mês para ver o detalhe abaixo</span>}
      </span>
      {anos.map(a => {
        const { antes, depois } = a.total;
        return (
          <React.Fragment key={a.ano}>
            <div className="impAno">
              <div>
                <div className="impAnoDet">% fechado em {a.ano}</div>
                <div className="impAnoNum">
                  <span className="de">{pct(antes.pct)}</span>
                  <span className="seta" aria-hidden="true">
                    →
                  </span>
                  <span className="para">{pct(depois.pct)}</span>
                  <span className="pp">{variacao(antes.pct, depois.pct)}</span>
                </div>
                <div className="impAnoDet">
                  {numero(antes.fechadas)} → <b>{numero(depois.fechadas)}</b> fechadas de{' '}
                  {numero(depois.a_operar)} a operar
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Barra antes={antes.pct} depois={depois.pct} grande />
                <div className="impLegenda">
                  <span>
                    <i style={{ background: '#94A3B8' }} />
                    Antes
                  </span>
                  <span>
                    <i style={{ background: '#16A34A' }} />
                    Depois
                  </span>
                  <span>
                    Liberadas {numero(antes.liberadas)} → {numero(depois.liberadas)} · Travadas{' '}
                    {numero(antes.travadas)} → {numero(depois.travadas)}
                  </span>
                </div>
              </div>
            </div>
            <div className="impTabela" style={{ maxHeight: 'none' }}>
              <table>
                <thead>
                  <tr>
                    <th>Mês</th>
                    <th className="num">A operar</th>
                    <th className="num">Fechadas antes</th>
                    <th className="num">% antes</th>
                    <th className="num">Fechadas depois</th>
                    <th className="num">% depois</th>
                    <th className="num">Variação</th>
                    <th>Evolução</th>
                  </tr>
                </thead>
                <tbody>
                  {a.meses.map(m => (
                    <tr key={m.competencia} {...linhaDoMes(m.competencia, mes, onMes)}>
                      <td className="nw">{mesAno(m.competencia)}</td>
                      <td className="num">{numero(m.depois.a_operar)}</td>
                      <td className="num">{numero(m.antes.fechadas)}</td>
                      <td className="num">{pct(m.antes.pct)}</td>
                      <td className="num">
                        <b>{numero(m.depois.fechadas)}</b>
                      </td>
                      <td className="num">
                        <b>{pct(m.depois.pct)}</b>
                      </td>
                      <td
                        className={`num${(m.depois.pct ?? 0) > (m.antes.pct ?? 0) ? ' pos' : ''}`}
                      >
                        {variacao(m.antes.pct, m.depois.pct)}
                      </td>
                      <td className="barraCel">
                        <Barra antes={m.antes.pct} depois={m.depois.pct} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Ano</td>
                    <td className="num">{numero(depois.a_operar)}</td>
                    <td className="num">{numero(antes.fechadas)}</td>
                    <td className="num">{pct(antes.pct)}</td>
                    <td className="num">{numero(depois.fechadas)}</td>
                    <td className="num">{pct(depois.pct)}</td>
                    <td className="num pos">{variacao(antes.pct, depois.pct)}</td>
                    <td className="barraCel">
                      <Barra antes={antes.pct} depois={depois.pct} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * Conferência da planilha por mês (05/11/2026): cada linha do arquivo cai em uma só
 * coluna, e a soma tem de dar as linhas da planilha. É o que responde "a planilha tem
 * 467 em janeiro e o calendário mostra 364 da área".
 */
export function Conferencia({
  porMes,
  mes,
  onMes,
}: {
  porMes: Record<string, IConferenciaDoMes>;
  mes: string;
  onMes: (competencia: string) => void;
}) {
  const meses = Object.entries(porMes);
  if (!meses.length) return null;
  const total = totalDaConferencia(porMes);
  // so as colunas que aparecem na planilha (a de outras e a de nao consideradas costumam
  // ficar zeradas)
  const colunas = COLUNAS_DA_CONFERENCIA.filter(
    c => total[c.chave] > 0 || c.chave === 'fechadas_agora' || c.chave === 'ignoradas',
  );
  const celulas = (m: IConferenciaDoMes) => (
    <>
      <td className="num">
        <b>{numero(m.linhas)}</b>
      </td>
      {colunas.map(c => (
        <td key={c.chave} className={`num conf-${c.chave}`}>
          {m[c.chave] ? numero(m[c.chave]) : <span className="zero">0</span>}
        </td>
      ))}
      <td className="num">
        {somaConfere(m) ? (
          <span className="impConfOk" title="A soma das colunas dá as linhas da planilha">
            ✓
          </span>
        ) : (
          <span className="impConfNao" title="A soma das colunas não dá as linhas da planilha">
            ≠
          </span>
        )}
      </td>
      <td className="num sub" title="A cascata do sem movimento: não são linhas da planilha">
        {m.fechadas_pelo_sistema_em_seguida ? numero(m.fechadas_pelo_sistema_em_seguida) : '—'}
      </td>
    </>
  );
  return (
    <div className="impSecao">
      <span className="impSub">
        Conferência da planilha por mês
        <span className="impDica"> · clique num mês para ver o detalhe abaixo</span>
      </span>
      <span className="impTexto">
        Cada linha da planilha cai em uma só coluna: a soma tem de dar as linhas do mês. No
        calendário, &ldquo;Área&rdquo; soma as fechadas agora e as já fechadas pela área;
        &ldquo;Sistema&rdquo; tem as que o sistema fechou antes, por não ter movimento.
      </span>
      <div className="impTabela" style={{ maxHeight: 'none' }}>
        <table>
          <thead>
            <tr>
              <th>Mês</th>
              <th className="num" title="Linhas da planilha com a competência do mês">
                Linhas na planilha
              </th>
              {colunas.map(c => (
                <th key={c.chave} className={`num conf-${c.chave}`} title={c.dica}>
                  {c.rotulo}
                </th>
              ))}
              <th className="num" title="A soma das colunas dá as linhas da planilha">
                Confere
              </th>
              <th
                className="num"
                title="Sem movimento que o sistema fechou logo depois, pela ordem dos meses: não são linhas da planilha"
              >
                + Sistema em seguida
              </th>
            </tr>
          </thead>
          <tbody>
            {meses.map(([competencia, m]) => (
              <tr key={competencia} {...linhaDoMes(competencia, mes, onMes)}>
                <td className="nw">{mesAno(competencia)}</td>
                {celulas(m)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Ano</td>
              {celulas(total)}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

type Lista = 'empresas' | 'ja' | 'sistema' | 'fora' | 'repetidas' | 'cancelamento';

/**
 * O resultado de uma importação: quem, quando e o arquivo; os números; o antes e o
 * depois; o detalhe empresa a empresa; e o cancelamento.
 */
export function PainelDaImportacao({
  registro,
  onAtualizado,
}: {
  registro: IRegistroDaImportacao;
  onAtualizado: (r: IRegistroDaImportacao) => void;
}) {
  const toast = useToast();
  const { resumo } = registro;
  const t = totaisDo(registro);
  // competencia escolhida na tabela de cima ("" = todas): as listas filtram por ela
  const [mes, setMes] = useState('');
  const listas = useRef<HTMLDivElement>(null);
  const escolherMes = (competencia: string) => {
    setMes(competencia);
    if (competencia) listas.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const todasEmpresas = useMemo(() => porEmpresa(resumo?.lista_fechadas ?? []), [resumo]);
  const empresas = useMemo(
    () => porEmpresa(daCompetencia(resumo?.lista_fechadas ?? [], mes)),
    [resumo, mes],
  );
  const fechadaPor = useMemo(
    () => new Map((resumo?.lista_fechadas ?? []).map(f => [`${f.codigo}|${f.competencia}`, f])),
    [resumo],
  );
  const sistema = daCompetencia(resumo?.lista_sistema ?? [], mes);
  const fora = daCompetencia(resumo?.lista_ignoradas ?? [], mes);
  const ja = daCompetencia(resumo?.lista_ja_fechadas ?? [], mes);
  const repetidas = daCompetencia(resumo?.lista_duplicadas ?? [], mes);
  const motivos = useMemo(() => motivosDeFora(fora), [fora]);
  const cancelamento = resumo?.cancelamento;
  const porMes = resumo?.por_mes;
  const conferenciaDoAno = useMemo(() => (porMes ? totalDaConferencia(porMes) : null), [porMes]);

  const [lista, setLista] = useState<Lista>('empresas');
  const [busca, setBusca] = useState('');
  const [motivo, setMotivo] = useState('');
  const [baixando, setBaixando] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [justificativa, setJustificativa] = useState('');

  const baixar = async (qual: 'relatorio' | 'arquivo') => {
    setBaixando(qual);
    try {
      if (qual === 'relatorio') await baixarRelatorioDaImportacao(registro.id);
      else await baixarArquivoDaImportacao(registro.id, registro.arquivo_nome);
    } catch {
      toast({ status: 'error', title: 'Não foi possível baixar', isClosable: true });
    } finally {
      setBaixando('');
    }
  };

  const cancelar = async () => {
    setCancelando(true);
    try {
      const r = await cancelarImportacao(registro.id, justificativa.trim());
      setConfirmar(false);
      setJustificativa('');
      onAtualizado(r);
      const c = r.resumo?.cancelamento;
      toast({
        status: 'success',
        title: 'Importação cancelada',
        description: c
          ? `${numero(c.desfeitas)} competências voltaram a abertas; ${numero(
              c.reabertas_pelo_sistema,
            )} reabertas pelo sistema em seguida.`
          : undefined,
        isClosable: true,
      });
    } catch (erro) {
      toast({
        status: 'error',
        title: 'Não foi possível cancelar',
        description: mensagemDoErro(erro),
        isClosable: true,
      });
    } finally {
      setCancelando(false);
    }
  };

  const empresasVisiveis = empresas.filter(e => confere(e, busca));
  const sistemaVisivel = sistema.filter(s => confere(s, busca));
  const foraVisivel = fora.filter(i => (!motivo || i.motivo_tipo === motivo) && confere(i, busca));
  const jaVisivel = ja.filter(j => confere(j, busca));
  const repetidasVisiveis = repetidas.filter(r => confere(r, busca));
  const desfeitas = daCompetencia(cancelamento?.lista_desfeitas ?? [], mes);
  const naoDesfeitas = daCompetencia(cancelamento?.lista_nao_desfeitas ?? [], mes);
  const sistemaDoCancelamento = daCompetencia(cancelamento?.lista_sistema ?? [], mes);
  const quem = registro.importado_por_nome || registro.importado_por;

  return (
    <>
      <div className="impFaixa">
        <div className="impQuem">
          <b>
            Importação nº {registro.id} · {quem}{' '}
            {registro.cancelada_em ? (
              <span className="impChip canc">Cancelada</span>
            ) : (
              <span className="impChip ok">Ativa</span>
            )}
          </b>
          <span>
            {dataEHora(registro.importado_em)} · {registro.importado_por}
          </span>
          <span>
            Arquivo:{' '}
            <button
              type="button"
              className="impLink"
              onClick={() => baixar('arquivo')}
              disabled={!!baixando}
              title="Baixar o arquivo original, como foi importado"
            >
              {registro.arquivo_nome}
            </button>{' '}
            ({tamanho(registro.arquivo_tamanho)}) ·{' '}
            <span title={`SHA-256 ${registro.arquivo_sha256}`}>
              SHA-256 {registro.arquivo_sha256.slice(0, 12)}…
            </span>
          </span>
        </div>
        <div className="impAcoes">
          <button
            type="button"
            className="impBtn"
            onClick={() => baixar('relatorio')}
            disabled={!!baixando}
          >
            <Baixar tam={13} traco={2.4} />
            {baixando === 'relatorio' ? 'Gerando…' : 'Baixar relatório (Excel)'}
          </button>
          {!registro.cancelada_em && !confirmar && (
            <button type="button" className="impBtn perigo" onClick={() => setConfirmar(true)}>
              Cancelar esta importação
            </button>
          )}
        </div>
      </div>

      {confirmar && (
        <div className="impCancelar" role="group" aria-label="Cancelar a importação">
          <span className="impTexto">
            <b>Cancelar a importação nº {registro.id}?</b> As competências que ela fechou e que
            ninguém mexeu depois voltam a abertas; o que o sistema fechou em seguida, fora de ordem,
            é reaberto. O que foi reaberto ou fechado de novo depois não é tocado. O registro e o
            arquivo continuam guardados para auditoria.
          </span>
          <textarea
            value={justificativa}
            onChange={e => setJustificativa(e.target.value)}
            placeholder="Motivo do cancelamento (obrigatório)"
            maxLength={500}
            aria-label="Motivo do cancelamento"
          />
          <div className="impAcoes fim">
            <button
              type="button"
              className="impBtn leve"
              onClick={() => setConfirmar(false)}
              disabled={cancelando}
            >
              Voltar
            </button>
            <button
              type="button"
              className="impBtn perigo cheio"
              onClick={cancelar}
              disabled={cancelando || justificativa.trim().length < MOTIVO_MINIMO}
            >
              {cancelando ? 'Cancelando…' : 'Confirmar cancelamento'}
            </button>
          </div>
        </div>
      )}

      {registro.cancelada_em && (
        <div className="impAviso">
          <b>
            Cancelada por {registro.cancelada_por_nome || registro.cancelada_por} em{' '}
            {dataEHora(registro.cancelada_em)}
          </b>
          <span>Motivo: {registro.cancelada_motivo}</span>
          {cancelamento && (
            <span>
              {numero(cancelamento.desfeitas)} competências voltaram a abertas
              {cancelamento.reabertas_pelo_sistema > 0 &&
                ` · ${numero(cancelamento.reabertas_pelo_sistema)} reabertas pelo sistema em seguida`}
              {cancelamento.nao_desfeitas > 0 &&
                ` · ${numero(cancelamento.nao_desfeitas)} não desfeitas (mudaram depois)`}
              .
            </span>
          )}
        </div>
      )}

      {resumo?.reconstruido && (
        <div className="impNota" role="note">
          Detalhe reconstruído a partir do arquivo original: esta importação é anterior à
          conferência por mês.
          {resumo.reconstrucao_confere === false &&
            ' O cadastro mudou desde então: o total de já fechadas não bate com o gravado na importação.'}
        </div>
      )}

      {resumo?.situacao === 'em_andamento' && (
        <div className="impAviso ambar">
          <b>Importação sem resumo</b>
          <span>O processamento não terminou de gravar o resultado.</span>
        </div>
      )}

      <div className="impSecao">
        <span className="impSub">Resumo</span>
        <div className="impKpis">
          <Kpi
            rotulo="Entregas lidas"
            valor={t.consideradas}
            detalhe={`de ${numero(t.linhas)} linhas do arquivo`}
            cor="#64748B"
          />
          <Kpi
            rotulo="Fechadas agora"
            valor={t.fechadas}
            detalhe={`em ${numero(todasEmpresas.length)} empresas`}
            cor="#16A34A"
          />
          <Kpi
            rotulo="Fechadas pelo sistema em seguida"
            valor={t.fechadas_pelo_sistema}
            detalhe="sem movimento, liberadas pela ordem"
            cor="#1B88F5"
          />
          <Kpi
            rotulo="Já estavam fechadas"
            valor={t.ja_fechadas}
            detalhe={
              conferenciaDoAno
                ? `área ${numero(conferenciaDoAno.ja_fechadas_area)} · sistema ${numero(
                    conferenciaDoAno.ja_fechadas_sistema,
                  )}${
                    conferenciaDoAno.ja_fechadas_outras
                      ? ` · outras ${numero(conferenciaDoAno.ja_fechadas_outras)}`
                      : ''
                  }`
                : undefined
            }
            cor="#94A3B8"
          />
          <Kpi
            rotulo="Ficaram de fora"
            valor={t.ignoradas}
            detalhe={
              motivos[0] ? `mais comum: ${MOTIVOS[motivos[0][0]] ?? motivos[0][0]}` : undefined
            }
            cor="#DC2626"
          />
        </div>
      </div>

      <AntesEDepois
        anos={resumo?.antes_depois ?? []}
        titulo="Antes x depois da importação"
        mes={mes}
        onMes={escolherMes}
      />
      {porMes && <Conferencia porMes={porMes} mes={mes} onMes={escolherMes} />}
      {cancelamento?.antes_depois && (
        <AntesEDepois
          anos={cancelamento.antes_depois}
          titulo="Antes x depois do cancelamento"
          mes={mes}
          onMes={escolherMes}
        />
      )}

      <div className="impSecao" ref={listas} style={{ scrollMarginTop: 12 }}>
        {mes && (
          <div className="impMesSel">
            <span>Mostrando só a competência</span>
            <button
              type="button"
              className="impChip mes"
              onClick={() => setMes('')}
              aria-label={`Limpar o filtro da competência ${mesAno(mes)}`}
              title="Ver todas as competências"
            >
              Competência {mesAno(mes)} <span aria-hidden="true">×</span>
            </button>
            {porMes?.[mes] && (
              <span className="impMesResumo">
                {numero(porMes[mes].linhas)} linhas na planilha ·{' '}
                {numero(porMes[mes].fechadas_agora)} fechadas agora ·{' '}
                {numero(
                  porMes[mes].ja_fechadas_area +
                    porMes[mes].ja_fechadas_sistema +
                    porMes[mes].ja_fechadas_outras,
                )}{' '}
                já fechadas · {numero(porMes[mes].ignoradas)} de fora
              </span>
            )}
          </div>
        )}
        <div className="impListas">
          <div className="grupo" role="tablist" aria-label="Detalhe da importação">
            {(
              [
                [
                  'empresas',
                  mes ? 'Fechadas agora' : 'Empresas fechadas agora',
                  mes ? daCompetencia(resumo?.lista_fechadas ?? [], mes).length : empresas.length,
                ],
                ...(resumo?.lista_ja_fechadas ? [['ja', 'Já estavam fechadas', ja.length]] : []),
                ['sistema', 'Fechadas pelo sistema em seguida', sistema.length],
                ['fora', 'Ficaram de fora', fora.length],
                ...(resumo?.lista_duplicadas?.length
                  ? [['repetidas', 'Linhas repetidas', repetidas.length]]
                  : []),
                ...(cancelamento ? [['cancelamento', 'Cancelamento', desfeitas.length]] : []),
              ] as [Lista, string, number][]
            ).map(([chave, rotulo, qtd]) => (
              <button
                key={chave}
                type="button"
                role="tab"
                aria-selected={lista === chave}
                className={`impLista${lista === chave ? ' on' : ''}`}
                onClick={() => setLista(chave)}
              >
                {rotulo}
                <span className="qtd">{numero(qtd)}</span>
              </button>
            ))}
          </div>
          <input
            className="impBusca"
            type="search"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por razão social, código ou CNPJ"
            aria-label="Buscar empresa"
          />
        </div>

        {lista === 'empresas' && (
          <div className="impTabela">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Razão social</th>
                  <th>CNPJ</th>
                  <th className="num">Competências</th>
                  <th>Meses fechados</th>
                  <th className="num" title="Fechadas com pendência aberta na revisão">
                    Com pendência
                  </th>
                  <th>Entregue por</th>
                </tr>
              </thead>
              <tbody>
                {empresasVisiveis.map(e => (
                  <tr key={e.codigo}>
                    <td className="cod">{e.codigo}</td>
                    <td className="razao">{e.razao || '—'}</td>
                    <td className="nw">{cnpjFormatado(e.cnpj)}</td>
                    <td className="num">{numero(e.competencias.length)}</td>
                    <td>
                      <span className="impMeses">
                        {e.competencias.map(c => {
                          const f = fechadaPor.get(`${e.codigo}|${c}`);
                          const trav = f?.situacao_anterior === 'Travada';
                          return (
                            <span
                              key={c}
                              className={`impMes${trav ? ' trav' : ''}`}
                              title={
                                f
                                  ? `${f.situacao_anterior} antes · entregue em ${dataCurta(
                                      f.entregue_em,
                                    )}${f.responsavel ? ` por ${f.responsavel}` : ''}${
                                      f.com_pendencia
                                        ? ` · com pendência (${f.criticas} crít., ${f.altas} altas)`
                                        : ''
                                    }`
                                  : undefined
                              }
                            >
                              {mesAno(c)}
                            </span>
                          );
                        })}
                      </span>
                    </td>
                    <td className="num">{numero(e.comPendencia)}</td>
                    <td>{e.responsaveis.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!empresasVisiveis.length && <div className="impVazio">Nenhuma empresa.</div>}
          </div>
        )}

        {lista === 'ja' && (
          <div className="impTabela">
            <table>
              <thead>
                <tr>
                  <th className="num">Linha</th>
                  <th>Código</th>
                  <th>Razão social</th>
                  <th>CNPJ</th>
                  <th>Competência</th>
                  <th>Fechada por</th>
                  <th>Quando</th>
                </tr>
              </thead>
              <tbody>
                {jaVisivel.map(j => (
                  <tr key={`${j.linha}-${j.competencia}`}>
                    <td className="num cod">{j.linha}</td>
                    <td className="cod">{j.codigo ?? '—'}</td>
                    <td className="razao">{j.razao || '—'}</td>
                    <td className="nw">{cnpjFormatado(j.cnpj)}</td>
                    <td className="nw">{mesAno(j.competencia)}</td>
                    <td>
                      <span className={`impChip ${j.coluna === 'sistema' ? 'neutro' : 'ok'}`}>
                        {j.fechada_por}
                      </span>
                    </td>
                    <td className="nw" title={j.por ?? undefined}>
                      {j.fechada_em ? dataEHora(j.fechada_em) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!jaVisivel.length && <div className="impVazio">Nenhuma já estava fechada.</div>}
          </div>
        )}

        {lista === 'repetidas' && (
          <div className="impTabela">
            <table>
              <thead>
                <tr>
                  <th className="num">Linha</th>
                  <th>Código</th>
                  <th>Razão social</th>
                  <th>CNPJ</th>
                  <th>Competência</th>
                  <th>Repete a linha</th>
                </tr>
              </thead>
              <tbody>
                {repetidasVisiveis.map(r => (
                  <tr key={`${r.linha}-${r.competencia}`}>
                    <td className="num cod">{r.linha}</td>
                    <td className="cod">{r.codigo ?? '—'}</td>
                    <td className="razao">{r.razao || '—'}</td>
                    <td className="nw">{cnpjFormatado(r.cnpj)}</td>
                    <td className="nw">{mesAno(r.competencia)}</td>
                    <td className="num">{r.linha_original}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!repetidasVisiveis.length && <div className="impVazio">Nenhuma linha repetida.</div>}
          </div>
        )}

        {lista === 'sistema' && (
          <div className="impTabela">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Razão social</th>
                  <th>Competência</th>
                  <th>O que o sistema fez</th>
                </tr>
              </thead>
              <tbody>
                {sistemaVisivel.map(s => (
                  <tr key={`${s.codigo}-${s.competencia}-${s.acao}`}>
                    <td className="cod">{s.codigo}</td>
                    <td className="razao">{s.razao || '—'}</td>
                    <td className="nw">{mesAno(s.competencia)}</td>
                    <td>{ACOES_DO_SISTEMA[s.acao] ?? s.acao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!sistemaVisivel.length && (
              <div className="impVazio">O sistema não fechou nada em seguida.</div>
            )}
          </div>
        )}

        {lista === 'fora' && (
          <>
            <div className="impFiltros" role="group" aria-label="Filtrar pelo motivo">
              <button
                type="button"
                className={`impFiltro${!motivo ? ' on' : ''}`}
                onClick={() => setMotivo('')}
                aria-pressed={!motivo}
              >
                Todos · {numero(fora.length)}
              </button>
              {motivos.map(([tipo, qtd]) => (
                <button
                  key={tipo}
                  type="button"
                  className={`impFiltro${motivo === tipo ? ' on' : ''}`}
                  onClick={() => setMotivo(motivo === tipo ? '' : tipo)}
                  aria-pressed={motivo === tipo}
                >
                  {MOTIVOS[tipo] ?? tipo} · {numero(qtd)}
                </button>
              ))}
            </div>
            <div className="impTabela">
              <table>
                <thead>
                  <tr>
                    <th className="num">Linha</th>
                    <th>Código</th>
                    <th>Razão social</th>
                    <th>CNPJ</th>
                    <th>Competência</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {foraVisivel.map(i => (
                    <tr key={`${i.linha}-${i.competencia}`}>
                      <td className="num cod">{i.linha}</td>
                      <td className="cod">{i.codigo ?? '—'}</td>
                      <td className="razao">{i.razao || '—'}</td>
                      <td className="nw">{cnpjFormatado(i.cnpj)}</td>
                      <td className="nw">{mesAno(i.competencia)}</td>
                      <td>{i.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!foraVisivel.length && <div className="impVazio">Nada ficou de fora.</div>}
            </div>
          </>
        )}

        {lista === 'cancelamento' && cancelamento && (
          <div className="impTabela">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Razão social</th>
                  <th>Competência</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {desfeitas
                  .filter(d => confere(d, busca))
                  .map(d => (
                    <tr key={`d-${d.codigo}-${d.competencia}`}>
                      <td className="cod">{d.codigo}</td>
                      <td className="razao">{d.razao || '—'}</td>
                      <td className="nw">{mesAno(d.competencia)}</td>
                      <td>Voltou a aberta</td>
                    </tr>
                  ))}
                {naoDesfeitas
                  .filter(d => confere(d, busca))
                  .map(d => (
                    <tr key={`n-${d.codigo}-${d.competencia}`}>
                      <td className="cod">{d.codigo}</td>
                      <td className="razao">{d.razao || '—'}</td>
                      <td className="nw">{mesAno(d.competencia)}</td>
                      <td>{d.motivo}</td>
                    </tr>
                  ))}
                {sistemaDoCancelamento
                  .filter(d => confere(d, busca))
                  .map(d => (
                    <tr key={`s-${d.codigo}-${d.competencia}`}>
                      <td className="cod">{d.codigo}</td>
                      <td className="razao">{d.razao || '—'}</td>
                      <td className="nw">{mesAno(d.competencia)}</td>
                      <td>{ACOES_DO_SISTEMA[d.acao] ?? d.acao} pelo sistema</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
